import { useQuery } from '@apollo/client';
import { createAxiosClient, ServiceProxyMap } from '@atlassian/temp-client-utils';
import {
  EntitlementDetails,
  FetchAvailablePromotionsForEntitlementQuery,
  FetchAvailablePromotionsForEntitlementQueryVariables,
  FetchEntitlementDetailsQuery,
  FetchEntitlementDetailsQueryVariables,
  FetchTransactionAccountForEntitlementQuery,
  FetchTransactionAccountForEntitlementQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import { pageViewStart, pageViewSuccess, UFOEventSource } from '@atlassian/temp-observability';
import {
  ENABLE_BLAZE_CACHE,
  ENABLE_RECENTLY_VISITED_HISTORY,
  removeContentScrollingHack,
} from '@atlassian/cssxp-utils-generic';
import { SIDEBAR_TASK_DETAIL, TaskDetailPage } from '@css-xp/apd-task-manager';
import {
  useFeatureToggle,
  useModalDialog,
  useRecentlyViewed,
  useTitle,
  useWarningModalDialog,
  withApollo,
} from '@css-xp/base';
import { getTransactionAccount, TransactionAccountContext, getCreditDetails } from '@css-xp/base/common-context';
import {
  AddCustomerContact,
  GetUpdatePaymentMethodLinkPane,
  SetCoBillDate,
  ViewPricing,
} from '@css-xp/base/common-sidebars';
import cloneDeep from 'lodash/cloneDeep';
import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router';
import { useAxios } from 'use-axios-client';
import {
  CancelActiveSubscription,
  CancelScheduledSubscription,
  ChangePricingPlanPane,
  DeleteProductData,
  EntitlementApplyPromotionPane,
  EntitlementExtendTrialPane,
  EntitlementMontlySubscriptionPane,
  EntitlementRemovePromotionPane,
  EntitlementRewindAndRebillPane,
  EntitlementSetInvoiceGroupPane,
  ManageEntitlementPane,
  OptoutTrial,
  ORDERDETAIL,
  RebillSubscription,
  StatuspageAnnualSubscriptionPane,
  SubscribeToAddOnsSidebar,
  SubscriptionTxATransfer,
  ViewDeletedProductDataDetails,
} from '../../../common/sidebar-panel';
import LinkedEntsWarningModal from '../../../common/sidebar-panel/warning-dialog';
import { EntitlementPageUtility } from '../../../entitlement-page-utility';
import { fetchAvailablePromotionsForEntitlementQuery } from '../queries/fetch-available-promotions-for-entitlement';
import { fetchEntitlementDetailsQuery } from '../queries/fetch-entitlement-details';
import { fetchTransactionAccountForEntitlement } from '../queries/fetch-transaction-account-for-entitlement';
import { EntitlementPageComponents } from './entitlement-page-components';
import { WarningModal } from './entitlement-page-utils';
import './entitlement-page.scss';

export enum SIDEBAR {
  STATUSPAGE_SWITCH_TO_ANNUAL_SUBSCRIPTION = 'statuspage-switch-to-annual-subscription',
  STATUSPAGE_SWITCH_TO_MONTHLY_SUBSCRIPTION = 'statuspage-switch-to-monthly-subscription',
  STATUSPAGE_CHANGE_PLAN = 'statuspage-change-plan',
  GET_UPDATE_PAYMENT_METHOD_LINK = 'get-update-payment-method-link',
  SUBSCRIBE_TO_ADD_ONS = 'subscribe-to-add-ons',
  CHANGE_PRICING_PLAN = 'apply-discounted-pricing',
  APPLY_PROMOTION = 'apply-promotion',
  EXTEND_TRIAL = 'extend-trial',
  CANCEL_ACTIVE_SUBSCRIPTION = 'cancel_active_subscription',
  CANCEL_SCHEDULED_BILLING_CHANGES = 'cancel_scheduled_billing_changes',
  REWIND_AND_REBILL_SUBSCRIPTION = 'rewind_and_rebill_subscription',
  REBILL_SUBSCRIPTION = 'rebill_subscription',
  OPTOUT_TRIAL = 'optout_trial',
  ORDER_DETAIL = 'order_detail',
  SET_INVOICE_GROUP = 'set_invoice_group',
  REMOVE_PROMOTION = 'remove-promotion',
  TRANSFER_ENTITLEMENT_TO_TRANSACTION_ACCOUNT = 'transferTransactionAccount',
  SET_BILL_DATE = 'coBillingSubscription',
  VIEW_PRICING_DETAILS = 'viewPricingDetails',
  DELETE_PRODUCT_DATA = 'deleteProductData',
  VIEW_DELETED_PRODUCT_DETAILS = 'viewDeletedProductDetails',
  ADD_CUSTOMER_CONTACT = 'add_customer_contact',
}

const allowedSidebars = {
  [SIDEBAR.STATUSPAGE_SWITCH_TO_ANNUAL_SUBSCRIPTION]: StatuspageAnnualSubscriptionPane,
  [SIDEBAR.STATUSPAGE_SWITCH_TO_MONTHLY_SUBSCRIPTION]: EntitlementMontlySubscriptionPane,
  [SIDEBAR.STATUSPAGE_CHANGE_PLAN]: ManageEntitlementPane,
  [SIDEBAR.GET_UPDATE_PAYMENT_METHOD_LINK]: GetUpdatePaymentMethodLinkPane,
  [SIDEBAR.SUBSCRIBE_TO_ADD_ONS]: SubscribeToAddOnsSidebar,
  [SIDEBAR.CHANGE_PRICING_PLAN]: ChangePricingPlanPane,
  [SIDEBAR.APPLY_PROMOTION]: EntitlementApplyPromotionPane,
  [SIDEBAR.EXTEND_TRIAL]: EntitlementExtendTrialPane,
  [SIDEBAR.CANCEL_SCHEDULED_BILLING_CHANGES]: CancelScheduledSubscription,
  [SIDEBAR.CANCEL_ACTIVE_SUBSCRIPTION]: CancelActiveSubscription,
  [SIDEBAR.REWIND_AND_REBILL_SUBSCRIPTION]: EntitlementRewindAndRebillPane,
  [SIDEBAR.REBILL_SUBSCRIPTION]: RebillSubscription,
  [SIDEBAR.OPTOUT_TRIAL]: OptoutTrial,
  [SIDEBAR.SET_BILL_DATE]: withApollo(SetCoBillDate),
  [SIDEBAR.ORDER_DETAIL]: ORDERDETAIL,
  [SIDEBAR.SET_INVOICE_GROUP]: EntitlementSetInvoiceGroupPane,
  [SIDEBAR.REMOVE_PROMOTION]: EntitlementRemovePromotionPane,
  [SIDEBAR.TRANSFER_ENTITLEMENT_TO_TRANSACTION_ACCOUNT]: SubscriptionTxATransfer,
  [SIDEBAR.VIEW_PRICING_DETAILS]: withApollo(ViewPricing),
  [SIDEBAR.DELETE_PRODUCT_DATA]: DeleteProductData,
  [SIDEBAR.VIEW_DELETED_PRODUCT_DETAILS]: ViewDeletedProductDataDetails,
  [SIDEBAR_TASK_DETAIL]: TaskDetailPage,
  [SIDEBAR.ADD_CUSTOMER_CONTACT]: AddCustomerContact,
};

export const EntitlementPage = ({
  appPageClass,
  title,
}: {
  appPageClass?: string;
  title?: string;
}): JSX.Element | null => {
  pageViewStart(UFOEventSource.ENTITLEMENT_PAGE);
  useEffect(() => {
    pageViewSuccess();
    removeContentScrollingHack();
  }, []);
  const { closeModalDialog, isModalOpen } = useModalDialog();
  const { closeWarningModalDialog, warningState } = useWarningModalDialog();
  const { id } = useParams<{ id: string }>();
  useTitle(id, title);
  const history = useHistory();
  const [isEnabled] = useFeatureToggle();
  const useCache = isEnabled(ENABLE_BLAZE_CACHE);
  const client = createAxiosClient(
    {
      baseURL: '/api/v1/entitlement/',
      transformResponse: [
        function (response) {
          const parsedResponse = JSON.parse(response);
          return parsedResponse.data;
        },
      ],
    },
    ServiceProxyMap.BLAZE
  );
  const fetchFromCache = (id: string) => {
    let response: any = {};
    // eslint-disable-next-line react-hooks/rules-of-hooks
    response = useAxios({
      axiosInstance: client,
      url: `${id}`,
    });
    response.previousData = null;
    return response;
  };

  const { loading, error, data, refetch: refetchStatusPage, previousData } = useCache
    ? fetchFromCache(id)
    : // eslint-disable-next-line react-hooks/rules-of-hooks
      useQuery<FetchEntitlementDetailsQuery, FetchEntitlementDetailsQueryVariables>(fetchEntitlementDetailsQuery, {
        variables: { id },
        onCompleted(data) {
          // run this for the first time only when no previous data is available and no hash is available in URL
          if (!previousData && data?.entitlementDetails?.scheduledChanges && !history.location.hash) {
            history.push({
              hash: '#scheduled-changes',
            });
          }
        },
      });

  const { updateRecentlyViewed } = useRecentlyViewed();
  React.useEffect(() => {
    if (data && isEnabled(ENABLE_RECENTLY_VISITED_HISTORY)) {
      updateRecentlyViewed({
        entityName: title?.toLowerCase().trim().replace(/\s+/g, '_'),
        metadata: {
          productId: data.entitlementDetails.productId,
          productName: data.entitlementDetails.productName,
          cloudSiteHostname: data.entitlementDetails.cloudSiteHostname,
        },
      });
    }
  }, [data, title]);

  const { data: availablePromotionData, refetch: refetchPromotions } = useQuery<
    FetchAvailablePromotionsForEntitlementQuery,
    FetchAvailablePromotionsForEntitlementQueryVariables
  >(fetchAvailablePromotionsForEntitlementQuery, {
    variables: { id },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const transactionAccountPageResult = useQuery<
    FetchTransactionAccountForEntitlementQuery,
    FetchTransactionAccountForEntitlementQueryVariables
  >(fetchTransactionAccountForEntitlement, {
    variables: { entitlementId: id },
    skip: !id,
  });

  if (loading) return <EntitlementPageUtility.Loading />;

  if (error && !data?.entitlementDetails) {
    return <EntitlementPageUtility.Error error={error} />;
  }
  if (error && data?.entitlementDetails) {
    // eslint-disable-next-line no-console
    console.error('Error on statuspage-page statuspage Entitlement', error);
  }

  if (!data?.entitlementDetails) {
    return null;
  }

  // Combines all the refetch happening on the entitlement page.
  // Earlier fetchAvailablePromotionsForEntitlementQuery was not getting refetched due to which on offfering update new promotions were not coming .
  // Refer Bug ID -> https://hello.atlassian.net/browse/TTC-5570
  const combineRefetch = () => {
    setTimeout(() => {
      refetchStatusPage();
      refetchPromotions();
    }, 1000);
  };

  const availablePromotions = availablePromotionData?.entitlementDetails?.availablePromotions;
  const statuspageEntitlement = {
    ...data?.entitlementDetails,
    availablePromotions: cloneDeep(availablePromotions),
  } as EntitlementDetails;

  const availablePaymentMethods =
    transactionAccountPageResult?.data?.entitlementDetails?.transactionAccount?.availablePaymentMethods?.nodes ?? [];
  const isPartnerAccount = !!transactionAccountPageResult?.data?.entitlementDetails?.transactionAccount
    ?.partnerAccountId;

  return (
    <EntitlementPageUtility
      allowedSidebars={allowedSidebars}
      entitlement={statuspageEntitlement}
      appPageClass={appPageClass}
    >
      <TransactionAccountContext.Provider
        value={{
          //@ts-ignore
          getTransactionAccount,
          getCreditDetails,
          ...transactionAccountPageResult,
        }}
      >
        <EntitlementPageComponents
          entitlement={statuspageEntitlement}
          refetchAll={combineRefetch}
          refetch={refetchStatusPage}
          availablePaymentMethods={availablePaymentMethods}
          isPartnerAccount={isPartnerAccount}
        />
        <WarningModal
          payload={{ entitlement: data?.entitlementDetails, refetch: combineRefetch }}
          onClose={closeModalDialog}
          isOpen={isModalOpen}
        />
        {warningState && (
          <LinkedEntsWarningModal
            header={warningState.header}
            description={warningState.description}
            okLabel={warningState.okLabel}
            onConfirm={warningState.onConfirm}
            onClose={closeWarningModalDialog}
            isOpen={warningState.isModalOpen}
          />
        )}
      </TransactionAccountContext.Provider>
    </EntitlementPageUtility>
  );
};
